import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './_drawer.scss';

type DrawerProps = {
  children: React.ReactNode;
  className?: string;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  onClose: () => void;
  open: boolean;
  position?: 'left' | 'right';
  showBackdrop?: boolean;
  width?: number | string;
};

function Drawer({
  children,
  className = '',
  footer,
  header,
  onClose,
  open,
  position = 'right',
  showBackdrop = true,
  width = 480,
}: DrawerProps): JSX.Element | null {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, handleKeyDown]);

  const widthValue = typeof width === 'number' ? `${width}px` : width;

  const content = (
    <>
      {showBackdrop && (
        <div
          aria-hidden="true"
          className={`drawer-overlay ${open ? 'drawer-overlay--open' : ''}`}
          onClick={onClose}
        />
      )}
      <div
        aria-modal="true"
        className={`drawer drawer--${position} ${open ? 'drawer--open' : ''} ${className}`}
        role="dialog"
        style={{ width: widthValue }}
      >
        {header && <div className="drawer__header">{header}</div>}
        <div className="drawer__content">{children}</div>
        {footer && <div className="drawer__footer">{footer}</div>}
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default Drawer;
